import React, { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Button,
  Box,
  Typography,
} from '@material-ui/core';
import { RootState } from '../../store/configureStore';
import * as AnalyticsActions from '../../actions/Analytics/actions';
import { MeasurementFilter, MeasurementFact, CHECK_TYPE_UNIT, CheckTypeName } from '../../constants/Analytics';
import { UserGenderMap, UserCareLevelMap } from '../../constants/User';

const genderLabel = (v: string | null) => (v == null ? '' : (UserGenderMap[Number(v)] ?? v));
const careLabel = (v: string | null) => (v == null ? '' : (UserCareLevelMap[Number(v)] ?? v));

const MeasurementListTab: FC<{ filter: MeasurementFilter }> = ({ filter }) => {
  const dispatch = useDispatch();
  const list = useSelector((s: RootState) => s.analytics.list);
  const [downloading, setDownloading] = useState(false);
  const unit = CHECK_TYPE_UNIT[filter.check_type as CheckTypeName];

  const changePage = (_: unknown, page: number) =>
    dispatch(AnalyticsActions.searchMeasurements(filter, page + 1, list.perPage));
  const changePerPage = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch(AnalyticsActions.searchMeasurements(filter, 1, Number(e.target.value)));

  const onCsv = async () => {
    setDownloading(true);
    try {
      await AnalyticsActions.downloadMeasurementsCsv(filter, list.page, list.perPage);
    } catch {
      alert('CSVダウンロードに失敗しました。');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body2" color="textSecondary">
          {list.count} 件（CSVは表示中ページ、最大500件を出力）
        </Typography>
        <Button color="primary" variant="outlined" onClick={onCsv} disabled={downloading || list.count === 0}>
          {downloading ? 'DL中…' : 'CSVダウンロード'}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>実施日時</TableCell>
              <TableCell>法人</TableCell>
              <TableCell>施設</TableCell>
              <TableCell>利用者</TableCell>
              <TableCell>年齢</TableCell>
              <TableCell>性別</TableCell>
              <TableCell>要介護度</TableCell>
              <TableCell>部位</TableCell>
              <TableCell align="right">スコア</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.items.map((r: MeasurementFact, i: number) => (
              <TableRow key={`${r.started_at}-${i}`}>
                <TableCell>{r.started_at}</TableCell>
                <TableCell>{r.company_name}</TableCell>
                <TableCell>{r.institution_name}</TableCell>
                <TableCell>{r.user_name}</TableCell>
                <TableCell>{r.age_at_measurement}</TableCell>
                <TableCell>{genderLabel(r.user_gender)}</TableCell>
                <TableCell>{careLabel(r.user_care_level)}</TableCell>
                <TableCell>{r.measurement_type ?? ''}</TableCell>
                <TableCell align="right">
                  {r.primary_value}
                  {unit}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={list.count}
        page={list.page - 1}
        onPageChange={changePage}
        rowsPerPage={list.perPage}
        onRowsPerPageChange={changePerPage}
        rowsPerPageOptions={[50, 100, 200, 500]}
      />
    </Box>
  );
};

export default MeasurementListTab;
